import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, useAuth } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import { Loader2, Shield, Save, Check, DollarSign, Trash2, UserMinus, UserPlus, Plus, Star, AlertTriangle } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [bets, setBets] = useState([]);
  const [crates, setCrates] = useState([]);
  const [users, setUsers] = useState([]);
  const [cfg, setCfg] = useState([]);
  const [msg, setMsg] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/", { replace: true }); }, [loading, user, nav]);

  const load = async () => {
    if (!user?.is_admin) return;
    try {
      const [o, b, c, u, cf] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/bets"),
        api.get("/admin/crates"),
        api.get("/admin/users"),
        api.get("/admin/crate-config"),
      ]);
      setOverview(o.data); setBets(b.data.bets); setCrates(c.data.crates); setUsers(u.data.users); setCfg(cf.data.config);
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Load failed" }); }
  };
  useEffect(() => { load(); }, [user]);

  const bootstrap = async () => {
    try { await api.post("/admin/bootstrap", { discord_id: user.discord_id }); window.location.reload(); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Bootstrap failed" }); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  if (!user.is_admin) {
    return (
      <AppShell>
        <div className="pt-16 px-6 flex items-center justify-center">
          <div className="glass rounded-3xl p-8 max-w-md w-full">
            <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center"><Shield className="w-5 h-5 text-white/70" /></div>
            <h1 className="mt-5 text-2xl font-bold">Admin access required</h1>
            <p className="mt-2 text-white/60 text-[14px]">If no admin exists yet, you can bootstrap yourself as the first admin.</p>
            <button onClick={bootstrap} className="mt-6 w-full py-3 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED]">Bootstrap as first admin</button>
            {msg && <div className="mt-4 text-red-300 text-[13px]">{msg.text}</div>}
            <Link to="/" className="mt-4 block text-center text-white/40 hover:text-white text-[13px]">Back home</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "reviews", label: `Stake reviews${overview?.pending_reviews ? ` (${overview.pending_reviews})` : ""}` },
    { id: "bets", label: `Bets${overview?.pending_bets ? ` (${overview.pending_bets})` : ""}` },
    { id: "crates", label: "Crates" },
    { id: "config", label: "Crate config" },
    { id: "users", label: "Users" },
    { id: "fake", label: "Leaderboard" },
    { id: "weekly", label: "Weekly prize" },
    { id: "news", label: "News CMS" },
    { id: "partner", label: "Partner inbox" },
  ];

  return (
    <AppShell>
      <div className="pt-10 md:pt-14 px-6 md:px-10 max-w-6xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[12px] uppercase tracking-[0.25em] text-white">Studio ops</div>
            <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-[-0.03em]">Admin</h1>
          </div>
        </div>

        <div className="mt-8 glass rounded-full p-1 inline-flex flex-wrap gap-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-[13px] transition-colors ${tab === t.id ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {msg && <div className={`mt-6 glass rounded-2xl p-4 text-[14px] ${msg.type === "ok" ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</div>}

        <div className="mt-8 pb-24">
          {tab === "overview" && overview && (
            <div className="space-y-4">
              <WhitelistBanner />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[["Users", overview.users], ["Bets", overview.bets], ["Crates", overview.crates], ["Pending delivery", overview.pending_delivery], ["Pending bets", overview.pending_bets || 0], ["Pending reviews", overview.pending_reviews || 0]].map(([k, v]) => (
                  <div key={k} className="glass rounded-2xl p-6">
                    <div className="text-[12px] uppercase tracking-widest text-white/40">{k}</div>
                    <div className="mt-2 text-4xl font-black">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "bets" && <BetsTable bets={bets} users={users} reload={load} setMsg={setMsg} />}
          {tab === "reviews" && <ReviewsTab reloadOverview={load} setMsg={setMsg} />}
          {tab === "crates" && <CratesTab crates={crates} users={users} reload={load} setMsg={setMsg} />}
          {tab === "config" && <ConfigEditor cfg={cfg} reload={load} setMsg={setMsg} />}
          {tab === "users" && <UsersTable users={users} reload={load} setMsg={setMsg} />}
          {tab === "fake" && <FakeLeaderboardTab setMsg={setMsg} />}
          {tab === "weekly" && <WeeklyPrizeTab users={users} setMsg={setMsg} reload={load} />}
          {tab === "news" && <NewsCmsTab setMsg={setMsg} />}
          {tab === "partner" && <PartnerInboxTab setMsg={setMsg} />}
        </div>
      </div>
    </AppShell>
  );
}

const cellSelect = "bg-black/40 border border-white/10 rounded-md px-2 py-1 text-[12px]";
const cellInput = "bg-black/40 border border-white/10 rounded-md px-2 py-1 text-[13px] w-24";

function BetsTable({ bets, users, reload, setMsg }) {
  const [validating, setValidating] = useState(null); // bet row or null

  const update = async (id, patch) => {
    try { await api.put(`/admin/bets/${id}`, patch); await reload(); setMsg({ type: "ok", text: "Bet updated" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  const copyStakeId = (b) => {
    const v = b.bet_url && /^https?:\/\//i.test(b.bet_url) ? b.bet_url : (b.bet_id || "");
    navigator.clipboard?.writeText(v).then(
      () => setMsg({ type: "ok", text: "Stake bet reference copied" }),
      () => setMsg({ type: "err", text: "Copy failed" }),
    );
  };
  const sorted = [...bets].sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1));
  return (
    <div className="glass rounded-3xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead className="text-[11px] uppercase tracking-widest text-white/40">
            <tr><th className="text-left p-4">ID</th><th className="text-left p-4">Stake ID</th><th className="text-left p-4">User</th><th className="text-left p-4">Game</th><th className="text-right p-4">Wager</th><th className="text-right p-4">Mult</th><th className="text-right p-4">Payout</th><th className="text-left p-4">Status</th><th className="text-left p-4">Actions</th></tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={9} className="p-5 text-white/40">No bets</td></tr>}
            {sorted.map((b) => (
              <tr key={b.id} className={`border-t hairline ${b.status === "pending" ? "bg-amber-400/[0.04]" : ""}`}>
                <td className="p-4 text-white/40">#{b.id}</td>
                <td className="p-4 text-white/70 font-mono text-[12px] max-w-[220px] truncate" title={b.bet_id || ""}>{b.bet_id || "—"}</td>
                <td className="p-4">{b.user_username}</td>
                <td className="p-4">{b.game || <span className="text-white/30">—</span>}</td>
                <td className="p-4 text-right">${(b.wager || 0).toFixed(2)}</td>
                <td className="p-4 text-right text-white">{(b.multiplier || 0).toFixed(1)}x</td>
                <td className="p-4 text-right">${(b.payout || 0).toFixed(2)}</td>
                <td className="p-4">
                  <span className="inline-flex items-center gap-2">
                    {b.status === "pending" && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#fbbf24", boxShadow: "0 0 8px #fbbf24" }} />}
                    <select value={b.status} onChange={(e) => update(b.id, { status: e.target.value })} className={cellSelect}>
                      <option value="pending">pending</option><option value="approved">approved</option><option value="rejected">rejected</option>
                    </select>
                  </span>
                </td>
                <td className="p-4 flex gap-3 flex-wrap">
                  {stakeBetUrl(b) && (
                    <a href={stakeBetUrl(b)} target="_blank" rel="noreferrer" className="text-white hover:text-white/70 text-[12px]">open ↗</a>
                  )}
                  <button onClick={() => copyStakeId(b)} className="text-white/60 hover:text-white text-[12px]">copy id</button>
                  <button onClick={() => setValidating(b)} className="text-white hover:text-white/70 text-[12px]">validate</button>
                  <button onClick={() => update(b.id, { admin_notes: prompt("Notes", b.admin_notes || "") || "" })} className="text-white/60 hover:text-white text-[12px]">notes</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {validating && <ValidateBetModal bet={validating} onClose={() => setValidating(null)} onDone={() => { setValidating(null); reload(); }} setMsg={setMsg} />}
    </div>
  );
}

function ValidateBetModal({ bet, onClose, onDone, setMsg }) {
  const [status, setStatus] = useState(bet.status === "pending" ? "approved" : bet.status || "approved");
  const [rarity, setRarity] = useState("common");
  const [amount, setAmount] = useState("");
  const [replaceCrate, setReplaceCrate] = useState(true);
  const [notes, setNotes] = useState(bet.admin_notes || "");
  const [game, setGame] = useState(bet.game || "");
  const [wager, setWager] = useState(bet.wager ?? "");
  const [mult, setMult] = useState(bet.multiplier ?? "");
  const [payout, setPayout] = useState(bet.payout ?? "");
  const [saving, setSaving] = useState(false);
  const betLink = stakeBetUrl(bet);

  const submit = async () => {
    setSaving(true);
    try {
      // Save the manually-verified bet details first (pending bets arrive empty).
      await api.put(`/admin/bets/${bet.id}`, {
        game: game || null,
        wager: wager === "" ? null : parseFloat(wager),
        multiplier: mult === "" ? null : parseFloat(mult),
        payout: payout === "" ? null : parseFloat(payout),
      });
      const body = {
        status,
        admin_notes: notes || null,
        crate_rarity: rarity,
        crate_reward_amount: amount === "" ? null : parseFloat(amount),
        replace_existing_crate: replaceCrate,
      };
      await api.post(`/admin/bets/${bet.id}/validate`, body);
      setMsg({ type: "ok", text: "Bet validated" });
      onDone();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(16px)" }} onClick={onClose}>
      <div className="glass-strong rounded-3xl p-7 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Validate bet</div>
          <button onClick={onClose} className="text-white/40 hover:text-white">Close</button>
        </div>
        <div className="mt-1 text-[12px] text-white/50">User: <b className="text-white">{bet.user_username}</b> &middot; {bet.game || "unverified details"} &middot; {(bet.multiplier || 0).toFixed(1)}x</div>
        {betLink && <a href={betLink} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[12px] text-white hover:text-white/70">Open bet on Stake in your browser ↗</a>}

        <div className="mt-5 space-y-3">
          <FieldRow label="Game (as verified on Stake)">
            <input value={game} onChange={(e) => setGame(e.target.value)} placeholder="e.g. HERETIC" className={cellInput + " w-full"} />
          </FieldRow>
          <div className="grid grid-cols-3 gap-2">
            <FieldRow label="Wager">
              <input type="number" step="0.01" value={wager} onChange={(e) => setWager(e.target.value)} className={cellInput + " w-full"} />
            </FieldRow>
            <FieldRow label="Mult">
              <input type="number" step="0.1" value={mult} onChange={(e) => setMult(e.target.value)} className={cellInput + " w-full"} />
            </FieldRow>
            <FieldRow label="Payout">
              <input type="number" step="0.01" value={payout} onChange={(e) => setPayout(e.target.value)} className={cellInput + " w-full"} />
            </FieldRow>
          </div>
          <FieldRow label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={cellSelect + " w-full"}>
              <option value="pending">pending</option><option value="approved">approved</option><option value="rejected">rejected</option>
            </select>
          </FieldRow>
          <FieldRow label="Crate rarity">
            <select value={rarity} onChange={(e) => setRarity(e.target.value)} className={cellSelect + " w-full"}>
              {["common", "rare", "epic", "legendary"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Reward amount (USD)">
            <input type="number" step="0.01" placeholder="e.g. 10.00" value={amount} onChange={(e) => setAmount(e.target.value)} className={cellInput + " w-full"} />
          </FieldRow>
          <label className="flex items-center gap-2 text-[13px] text-white/70">
            <input type="checkbox" checked={replaceCrate} onChange={(e) => setReplaceCrate(e.target.checked)} />
            Replace existing crate (if any)
          </label>
          <FieldRow label="Admin notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={cellInput + " w-full"} />
          </FieldRow>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-full glass text-[13px]">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-5 py-2 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
            {saving ? "Saving..." : "Validate & assign"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}

// Build an openable Stake bet URL from whatever reference we stored
// (full URL, bare UUID, or house:/user:/casino: iid).
function stakeBetUrl(b) {
  const raw = String(b.bet_url || b.bet_id || "").trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}$/i.test(raw)) {
    return `https://stake.com/?betId=${raw}`;
  }
  if (/^(house|user|casino|sport|game):\d+/i.test(raw)) {
    return `https://stake.com/?iid=${encodeURIComponent(raw)}`;
  }
  const m = raw.match(/[?&](iid|betId|bet)=([^&]+)/);
  if (m) return raw.startsWith("http") ? raw : `https://stake.com/${raw.startsWith("/") ? "" : "/"}${raw}`;
  return null;
}

function CratesTab({ crates, users, reload, setMsg }) {
  return (
    <div className="space-y-5">
      <CreateCrateForm users={users} reload={reload} setMsg={setMsg} />
      <CratesTable crates={crates} reload={reload} setMsg={setMsg} />
    </div>
  );
}

function CreateCrateForm({ users, reload, setMsg }) {
  const [userId, setUserId] = useState("");
  const [rarity, setRarity] = useState("common");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) { setMsg({ type: "err", text: "Pick a user" }); return; }
    setSaving(true);
    try {
      await api.post("/admin/crates", {
        user_id: parseInt(userId),
        rarity,
        reward_amount: parseFloat(amount || "0"),
        reward_type: "stake_tip",
        admin_notes: notes || null,
      });
      setMsg({ type: "ok", text: "Crate created" });
      setAmount(""); setNotes("");
      await reload();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="glass rounded-3xl p-6">
      <div className="text-[12px] uppercase tracking-widest text-white/50">Create & assign crate</div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">User</div>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className={cellSelect + " w-full"}>
            <option value="">Select user</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.global_name || u.username}{u.stake_username ? ` · @${u.stake_username}` : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Rarity</div>
          <select value={rarity} onChange={(e) => setRarity(e.target.value)} className={cellSelect + " w-full"}>
            {["common", "rare", "epic", "legendary"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Amount (USD)</div>
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={cellInput + " w-full"} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Notes</div>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className={cellInput + " w-full"} />
        </div>
      </div>
      <div className="mt-4">
        <button disabled={saving} className="px-5 py-2 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
          {saving ? "Creating..." : "Create crate"}
        </button>
      </div>
    </form>
  );
}

function CratesTable({ crates, reload, setMsg }) {
  const update = async (id, patch) => {
    try { await api.put(`/admin/crates/${id}`, patch); await reload(); setMsg({ type: "ok", text: "Crate updated" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this crate? This cannot be undone.")) return;
    try { await api.delete(`/admin/crates/${id}`); await reload(); setMsg({ type: "ok", text: "Crate deleted" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  return (
    <div className="glass rounded-3xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead className="text-[11px] uppercase tracking-widest text-white/40">
            <tr><th className="text-left p-4">User</th><th className="text-left p-4">Stake</th><th className="text-left p-4">Rarity</th><th className="text-right p-4">Reward</th><th className="text-left p-4">Opened</th><th className="text-left p-4">Delivery</th><th className="text-left p-4">Actions</th></tr>
          </thead>
          <tbody>
            {crates.length === 0 && <tr><td colSpan={7} className="p-5 text-white/40">No crates</td></tr>}
            {crates.map((c) => (
              <tr key={c.id} className="border-t hairline">
                <td className="p-4">{c.user_username}</td>
                <td className="p-4 text-white/60">{c.stake_username || "-"}</td>
                <td className="p-4">
                  <select value={c.rarity} onChange={(e) => update(c.id, { rarity: e.target.value })} className={cellSelect}>
                    {["common", "rare", "epic", "legendary"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-4 text-right">
                  <input type="number" step="0.01" defaultValue={c.reward_amount} onBlur={(e) => update(c.id, { reward_amount: parseFloat(e.target.value) })} className={`${cellInput} text-right`} />
                </td>
                <td className="p-4">{c.opened ? <Check className="w-4 h-4 text-emerald-400" /> : <span className="text-white/30 text-[12px]">no</span>}</td>
                <td className="p-4">
                  <select value={c.delivery_status} onChange={(e) => update(c.id, { delivery_status: e.target.value })} className={cellSelect}>
                    <option value="pending">pending</option><option value="processing">processing</option><option value="paid">paid</option><option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td className="p-4 flex gap-3 items-center">
                  <button onClick={() => update(c.id, { mark_paid: true })} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-[12px]"><DollarSign className="w-3 h-3" />Mark paid</button>
                  <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-red-300 hover:text-red-200 text-[12px]"><Trash2 className="w-3 h-3" />Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConfigEditor({ cfg, reload, setMsg }) {
  const [rows, setRows] = useState(cfg);
  useEffect(() => { setRows(cfg); }, [cfg]);
  const set = (i, k, v) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const save = async () => {
    try {
      const payload = rows.map((r) => ({
        rarity: r.rarity,
        probability: parseFloat(r.probability),
        min_reward: parseFloat(r.min_reward),
        max_reward: parseFloat(r.max_reward),
        color: r.color,
        reveal_theme: r.reveal_theme || "neon",
      }));
      await api.put("/admin/crate-config", payload);
      setMsg({ type: "ok", text: "Config saved" }); await reload();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  const totalProb = rows.reduce((s, r) => s + parseFloat(r.probability || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[12px] uppercase tracking-widest text-white/50">Rarity · probability · reward range · reveal theme</div>
        <div className={`text-[12px] ${Math.abs(totalProb - 1) < 0.01 ? "text-emerald-300" : "text-amber-300"}`}>Sum: {totalProb.toFixed(3)}</div>
      </div>
      <div className="glass rounded-3xl overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead className="text-[11px] uppercase tracking-widest text-white/40">
            <tr><th className="text-left p-4">Rarity</th><th className="text-left p-4">Probability</th><th className="text-left p-4">Min reward</th><th className="text-left p-4">Max reward</th><th className="text-left p-4">Color</th><th className="text-left p-4">Reveal theme</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.rarity} className="border-t hairline">
                <td className="p-4 uppercase">{r.rarity}</td>
                <td className="p-4"><input type="number" step="0.01" value={r.probability} onChange={(e) => set(i, "probability", e.target.value)} className={cellInput} /></td>
                <td className="p-4"><input type="number" step="0.01" value={r.min_reward} onChange={(e) => set(i, "min_reward", e.target.value)} className={cellInput} /></td>
                <td className="p-4"><input type="number" step="0.01" value={r.max_reward} onChange={(e) => set(i, "max_reward", e.target.value)} className={cellInput} /></td>
                <td className="p-4"><input value={r.color || ""} onChange={(e) => set(i, "color", e.target.value)} className={cellInput} /></td>
                <td className="p-4">
                  <select value={r.reveal_theme || "neon"} onChange={(e) => set(i, "reveal_theme", e.target.value)} className={cellSelect}>
                    <option value="neon">neon</option>
                    <option value="gothic">gothic</option>
                    <option value="arcade">arcade</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={save} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED]"><Save className="w-4 h-4" />Save config</button>
    </div>
  );
}

function UsersTable({ users, reload, setMsg }) {
  const patch = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); await reload(); setMsg({ type: "ok", text: "User updated" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  return (
    <div className="glass rounded-3xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead className="text-[11px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">User</th>
              <th className="text-left p-4">Discord ID</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Stake</th>
              <th className="text-left p-4">Public</th>
              <th className="text-left p-4">Admin</th>
              <th className="text-left p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t hairline">
                <td className="p-4 flex items-center gap-2">{u.avatar && <img src={u.avatar} className="w-6 h-6 rounded-full" alt="" />}{u.global_name || u.username}</td>
                <td className="p-4 text-white/60">{u.discord_id}</td>
                <td className="p-4">{u.email || "-"}</td>
                <td className="p-4">{u.stake_username ? (<span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: u.stake_verified ? "#22c55e" : "#fbbf24" }} title={u.stake_verified ? "verified" : "unverified"} />@{u.stake_username}</span>) : <span className="text-white/30">—</span>}</td>
                <td className="p-4">{u.is_public !== 0 ? "yes" : <span className="text-white/40">no</span>}</td>
                <td className="p-4">{u.is_admin ? <Shield className="w-4 h-4 text-white" /> : "-"}</td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    {u.is_admin ? (
                      <button onClick={() => patch(u.id, { is_admin: false })} className="inline-flex items-center gap-1 text-white/70 hover:text-white text-[12px]"><UserMinus className="w-3 h-3" />Revoke admin</button>
                    ) : (
                      <button onClick={() => patch(u.id, { is_admin: true })} className="inline-flex items-center gap-1 text-white hover:text-white/70 text-[12px]"><UserPlus className="w-3 h-3" />Make admin</button>
                    )}
                    {u.stake_username && (
                      <button onClick={() => window.confirm(`Unlink Stake account for ${u.username}?`) && patch(u.id, { unlink_stake: true })} className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-[12px]"><Trash2 className="w-3 h-3" />Unlink Stake</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FakeLeaderboardTab({ setMsg }) {
  const [entries, setEntries] = useState([]);
  const [liveBoard, setLiveBoard] = useState([]);
  const [form, setForm] = useState({ display_name: "", stake_username: "", avatar: "", game: "HERETIC", wager: "1.00", multiplier: "500", payout: "500.00" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/fake-leaderboard"); setEntries(r.data.entries || []); } catch {}
    try { const r = await api.get("/leaderboard?period=week&limit=20"); setLiveBoard(r.data.leaderboard || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/fake-leaderboard", {
        display_name: form.display_name,
        stake_username: form.stake_username || null,
        avatar: form.avatar || null,
        game: form.game,
        wager: parseFloat(form.wager) || 0,
        multiplier: parseFloat(form.multiplier) || 0,
        payout: parseFloat(form.payout) || 0,
      });
      setMsg({ type: "ok", text: "Entry added — visible on leaderboard now" });
      setForm({ ...form, display_name: "", stake_username: "", avatar: "" });
      await load();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete fake entry?")) return;
    try { await api.delete(`/admin/fake-leaderboard/${id}`); await load(); setMsg({ type: "ok", text: "Deleted" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  const hideReal = async (entry) => {
    const betId = entry.bet_id;
    if (!betId) return;
    if (!window.confirm(`Hide ${entry.username || entry.global_name}'s ${entry.multiplier?.toFixed?.(1) ?? entry.multiplier}x entry? This will reject their bet.`)) return;
    try {
      const r = await api.get("/admin/bets");
      const match = (r.data.bets || []).find(b => b.bet_id === betId);
      if (!match) { setMsg({ type: "err", text: "Bet not found" }); return; }
      await api.put(`/admin/bets/${match.id}`, { status: "rejected" });
      setMsg({ type: "ok", text: "Hidden from leaderboard" });
      await load();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  const games = ["HERETIC", "PUFFY", "GATES OF BELLATRO: HOLD'EM", "TOWN TYCOON 3D", "CHEESTY CENT", "DIAL-UP DEATH", "CHEESE CAPONE", "ICEBOY", "AGENDA ONE", "WANTED: OCEAN DRIVE", "EYES WILD SHUT", "MONEY HAWK: PRO STAKER", "MONEY HAWK 3D", "DEAD DRAW SHOWDOWN"];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const startEdit = (e) => {
    setEditing(e.id);
    setEditForm({ game: e.game, multiplier: String(e.multiplier ?? ""), payout: String(e.payout ?? ""), wager: String(e.wager ?? "") });
  };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/fake-leaderboard/${id}`, {
        game: editForm.game,
        multiplier: parseFloat(editForm.multiplier) || 0,
        payout: parseFloat(editForm.payout) || 0,
        wager: parseFloat(editForm.wager) || 0,
      });
      setMsg({ type: "ok", text: "Updated" });
      setEditing(null);
      await load();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  const moveEntry = async (index, dir) => {
    const sorted = [...entries].sort((a, b) => (b.multiplier || 0) - (a.multiplier || 0) || (b.payout || 0) - (a.payout || 0));
    const targetIdx = index + dir;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const cur = sorted[index];
    const other = sorted[targetIdx];
    try {
      await api.put(`/admin/fake-leaderboard/${cur.id}`, { multiplier: other.multiplier, payout: other.payout });
      await api.put(`/admin/fake-leaderboard/${other.id}`, { multiplier: cur.multiplier, payout: cur.payout });
      await load();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  return (
    <div className="space-y-5">
      <div className="glass rounded-3xl overflow-hidden">
        <div className="p-5 border-b hairline">
          <div className="text-[12px] uppercase tracking-widest text-white/50">Live leaderboard preview (Top 20 · 7d)</div>
          <div className="text-[12px] text-white/40 mt-1">This is exactly what players see. Hide real entries or add seeded ones to curate it.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-[11px] uppercase tracking-widest text-white/40">
              <tr><th className="text-left p-3 w-8">#</th><th className="text-left p-3">Player</th><th className="text-left p-3">Game</th><th className="text-right p-3">Mult</th><th className="text-right p-3">Payout</th><th className="text-left p-3">Source</th><th className="text-left p-3">Action</th></tr>
            </thead>
            <tbody>
              {liveBoard.length === 0 && <tr><td colSpan={7} className="p-5 text-white/40">Empty — no bets yet</td></tr>}
              {liveBoard.map((r, i) => (
                <tr key={(r.bet_id || r.username || i) + String(i)} className="border-t hairline">
                  <td className="p-3 text-white/50">{i + 1}</td>
                  <td className="p-3">{r.global_name || r.username}{r.stake_username ? <span className="text-white/40 text-[11px]"> @{r.stake_username}</span> : null}</td>
                  <td className="p-3 text-white/70">{r.game}</td>
                  <td className="p-3 text-right text-white font-semibold">{(r.multiplier || 0).toFixed(1)}x</td>
                  <td className="p-3 text-right">${(r.payout || 0).toFixed(2)}</td>
                  <td className="p-3"><span className={`text-[10px] px-2 py-1 rounded-full ${r.fake ? "bg-amber-400/10 text-amber-300" : "bg-white/5 text-white/60"}`}>{r.fake ? "seeded" : "real"}</span></td>
                  <td className="p-3">
                    {r.fake ? <span className="text-white/30 text-[11px]">delete below</span> :
                      <button onClick={() => hideReal(r)} className="text-red-300 hover:text-red-200 text-[11px]">Hide</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <form onSubmit={create} className="glass rounded-3xl p-6">
        <div className="text-[12px] uppercase tracking-widest text-white/50 flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Add / seed leaderboard entry</div>
        <div className="text-[12px] text-white/40 mt-1">Seeded entries appear instantly, ranked by multiplier. Use them to fill or curate the board.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Display name"><input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} required className={cellInput + " w-full"} /></Field>
          <Field label="Stake handle (optional)"><input value={form.stake_username} onChange={(e) => set("stake_username", e.target.value)} placeholder="@handle" className={cellInput + " w-full"} /></Field>
          <Field label="Avatar URL (optional)"><input value={form.avatar} onChange={(e) => set("avatar", e.target.value)} placeholder="https://..." className={cellInput + " w-full"} /></Field>
          <Field label="Game">
            <select value={form.game} onChange={(e) => set("game", e.target.value)} className={cellSelect + " w-full"}>
              {games.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Wager ($)"><input type="number" step="0.01" value={form.wager} onChange={(e) => set("wager", e.target.value)} className={cellInput + " w-full"} /></Field>
          <Field label="Multiplier (x)"><input type="number" step="0.1" value={form.multiplier} onChange={(e) => set("multiplier", e.target.value)} className={cellInput + " w-full"} /></Field>
          <Field label="Payout ($)"><input type="number" step="0.01" value={form.payout} onChange={(e) => set("payout", e.target.value)} className={cellInput + " w-full"} /></Field>
        </div>
        <button disabled={saving} className="mt-5 px-5 py-2 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
          {saving ? "Adding..." : "Add entry"}
        </button>
      </form>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="p-4 border-b hairline text-[12px] uppercase tracking-widest text-white/40">Seeded entries only — edit to curate ranking (sorted by mult)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="text-[11px] uppercase tracking-widest text-white/40">
              <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Game</th><th className="text-right p-4">Wager</th><th className="text-right p-4">Mult</th><th className="text-right p-4">Payout</th><th className="text-left p-4">Placed</th><th className="text-center p-4">Order</th><th className="text-left p-4">Actions</th></tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={8} className="p-5 text-white/40">No seeded entries yet — add one above</td></tr>}
              {[...entries].sort((a,b) => (b.multiplier||0)-(a.multiplier||0) || (b.payout||0)-(a.payout||0)).map((e, idx, arr) => (
                <tr key={e.id} className="border-t hairline">
                  <td className="p-4">{e.display_name}{e.stake_username ? <span className="text-white/40 text-[11px]"> @{e.stake_username}</span> : null}</td>
                  <td className="p-4">
                    {editing === e.id ? (
                      <select value={editForm.game} onChange={(ev) => setEditForm({...editForm, game: ev.target.value})} className={cellSelect + " w-full"}>
                        {games.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    ) : e.game}
                  </td>
                  <td className="p-4 text-right">
                    {editing === e.id ? <input value={editForm.wager} onChange={ev => setEditForm({...editForm, wager: ev.target.value})} className={cellInput + " w-20 text-right"} /> : `$${(e.wager || 0).toFixed(2)}`}
                  </td>
                  <td className="p-4 text-right">
                    {editing === e.id ? <input type="number" step="0.1" value={editForm.multiplier} onChange={ev => setEditForm({...editForm, multiplier: ev.target.value})} className={cellInput + " w-20 text-right"} /> : <span className="text-white">{(e.multiplier || 0).toFixed(1)}x</span>}
                  </td>
                  <td className="p-4 text-right">
                    {editing === e.id ? <input type="number" step="0.01" value={editForm.payout} onChange={ev => setEditForm({...editForm, payout: ev.target.value})} className={cellInput + " w-20 text-right"} /> : `$${(e.payout || 0).toFixed(2)}`}
                  </td>
                  <td className="p-4 text-white/50">{e.placed_at?.slice(0, 16)}</td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button disabled={idx === 0} onClick={() => moveEntry(idx, -1)} className="px-1.5 py-1 text-white/60 hover:text-white disabled:opacity-20">▲</button>
                      <button disabled={idx === arr.length-1} onClick={() => moveEntry(idx, 1)} className="px-1.5 py-1 text-white/60 hover:text-white disabled:opacity-20">▼</button>
                    </div>
                  </td>
                  <td className="p-4">
                    {editing === e.id ? (
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(e.id)} className="text-emerald-300 text-[12px]">Save</button>
                        <button onClick={cancelEdit} className="text-white/60 text-[12px]">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(e)} className="text-white/70 hover:text-white text-[12px]">Edit</button>
                        <button onClick={() => remove(e.id)} className="inline-flex items-center gap-1 text-red-300 text-[12px]"><Trash2 className="w-3 h-3" />Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function WeeklyPrizeTab({ users, reload, setMsg }) {
  const [current, setCurrent] = useState(null);
  const [latest, setLatest] = useState(null);
  const [weekStart, setWeekStart] = useState("");
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("50.00");
  const [rarity, setRarity] = useState("legendary");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/weekly-prize/current");
      setCurrent(r.data.current); setLatest(r.data.latest); setWeekStart(r.data.current_week_start);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const award = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/weekly-prize/award", {
        user_id: userId ? parseInt(userId) : null,
        reward_amount: parseFloat(amount) || 0,
        rarity,
      });
      setMsg({ type: "ok", text: "Weekly prize awarded" });
      await load(); await reload();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="glass rounded-3xl p-7">
        <div className="text-[12px] uppercase tracking-widest text-white/50 flex items-center gap-2"><Star className="w-3.5 h-3.5 text-[#FFD166]" /> Current week</div>
        <div className="mt-3 text-3xl font-black tracking-tight">Week of {weekStart}</div>
        {current ? (
          <div className="mt-3 text-white/70 text-[14px]">Awarded to <b className="text-white">{current.winner_display_name}</b> — crate #{current.crate_id}, top mult {(current.top_multiplier || 0).toFixed(1)}x</div>
        ) : (
          <div className="mt-3 text-white/50 text-[14px]">No prize awarded this week yet.</div>
        )}
      </div>

      <form onSubmit={award} className="glass rounded-3xl p-7">
        <div className="text-[12px] uppercase tracking-widest text-white/50">Award weekly prize</div>
        <div className="mt-1 text-[13px] text-white/50">Leave user blank to auto-select the top multiplier in the last 7 days.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Winner (optional)">
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className={cellSelect + " w-full"}>
              <option value="">Auto (top of leaderboard)</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.global_name || u.username}</option>)}
            </select>
          </Field>
          <Field label="Rarity">
            <select value={rarity} onChange={(e) => setRarity(e.target.value)} className={cellSelect + " w-full"}>
              {["common", "rare", "epic", "legendary"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Reward amount ($)"><input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={cellInput + " w-full"} /></Field>
        </div>
        <button disabled={saving} className="mt-5 px-5 py-2 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
          {saving ? "Awarding..." : "Award bonus crate"}
        </button>
      </form>

      {latest && (
        <div className="glass rounded-3xl p-6">
          <div className="text-[12px] uppercase tracking-widest text-white/50">Most recent prize</div>
          <div className="mt-2 text-[14px] text-white/80">Week of <b>{latest.week_start}</b> → <b>{latest.winner_display_name}</b>, crate #{latest.crate_id}</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}

function WhitelistBanner() {
  const [dismissed, setDismissed] = React.useState(
    typeof window !== "undefined" && window.localStorage.getItem("gumble_whitelist_dismissed") === "1"
  );
  if (dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem("gumble_whitelist_dismissed", "1"); } catch {}
  };
  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-4 h-4 text-white/70" />
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-white">Stake API is behind Cloudflare</div>
          <p className="mt-1 text-[13px] text-white/70 leading-relaxed">
            Server-to-server requests from a shared cloud IP are challenged by Cloudflare, so <b>link-stake</b> and
            <b> /bets/sync</b> may respond &quot;Stake blocked the request&quot; from this preview environment.
          </p>
          <div className="mt-3 text-[12px] text-white/70 space-y-1.5">
            <div>&mdash; Deploy the backend on a host with a residential-grade or whitelisted IP.</div>
            <div>&mdash; Ensure <span className="font-mono text-white">STAKE_ACCESS_TOKEN</span> is set in the backend environment.</div>
            <div>&mdash; If Cloudflare still challenges, contact Stake support to whitelist your server IP for your affiliate account.</div>
            <div>&mdash; A quick smoke test:
              &nbsp;<span className="font-mono text-white">curl -X POST https://stake.com/_api/graphql -H &quot;x-access-token: $TOKEN&quot; -H &quot;origin: https://stake.com&quot; -d &#39;&#123;&quot;query&quot;:&quot;&#123; user &#123; name &#125; &#125;&quot;&#125;&#39;</span>
            </div>
          </div>
        </div>
        <button onClick={dismiss} className="text-[11px] text-white/40 hover:text-white">dismiss</button>
      </div>
    </div>
  );
}


function NewsCmsTab({ setMsg }) {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const r = await api.get("/admin/news"); setArticles(r.data.articles || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/admin/news/generate", { topic: topic.trim() || null });
      setMsg({ type: "ok", text: `Article generated: ${r.data.article.title}` });
      setTopic("");
      await load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to generate" });
    } finally { setGenerating(false); }
  };

  const togglePublished = async (a) => {
    try { await api.patch(`/admin/news/${a.id}`, { published: a.published ? false : true }); await load(); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/news/${a.id}`); await load(); setMsg({ type: "ok", text: "Article deleted" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  return (
    <div className="space-y-5" data-testid="news-cms-tab">
      <div className="glass rounded-3xl p-6">
        <div className="text-[12px] uppercase tracking-widest text-white/50">AI article generator</div>
        <div className="mt-1 text-[13px] text-white/50">Leave the topic blank to pick a random SEO angle. The article is generated with Claude Sonnet 4.6 via the Emergent LLM key.</div>
        <div className="mt-4 flex flex-col md:flex-row gap-3">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Why buy-bonus features hurt low-roll bankrolls"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[13.5px] outline-none focus:border-[#0A84FF]/70" />
          <button onClick={generate} disabled={generating} data-testid="generate-article-btn"
            className="px-5 py-3 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
            {generating ? "Generating..." : "Generate article"}
          </button>
        </div>
      </div>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="text-[11px] uppercase tracking-widest text-white/40">
              <tr><th className="text-left p-4">Title</th><th className="text-left p-4">Slug</th><th className="text-left p-4">Tags</th><th className="text-left p-4">Published</th><th className="text-left p-4">Created</th><th className="text-left p-4">Actions</th></tr>
            </thead>
            <tbody>
              {articles.length === 0 && <tr><td colSpan={6} className="p-5 text-white/40">No articles yet</td></tr>}
              {articles.map((a) => (
                <tr key={a.id} className="border-t hairline">
                  <td className="p-4 max-w-md truncate">{a.title}</td>
                  <td className="p-4 text-white/60 font-mono text-[12px]">{a.slug}</td>
                  <td className="p-4 text-white/60 text-[12px]">{a.tags || "-"}</td>
                  <td className="p-4">{a.published ? <span className="text-emerald-300 text-[12px]">yes</span> : <span className="text-white/40 text-[12px]">no</span>}</td>
                  <td className="p-4 text-white/50 text-[12px]">{(a.created_at || "").slice(0, 10)}</td>
                  <td className="p-4 flex gap-3">
                    <button onClick={() => setEditing(a)} className="text-white hover:text-white/70 text-[12px]">edit</button>
                    <button onClick={() => togglePublished(a)} className="text-white/60 hover:text-white text-[12px]">{a.published ? "unpublish" : "publish"}</button>
                    <button onClick={() => remove(a)} className="text-red-300 hover:text-red-200 text-[12px]">delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditArticleModal article={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); setMsg({ type: "ok", text: "Article saved" }); }} setMsg={setMsg} />
      )}
    </div>
  );
}

function EditArticleModal({ article, onClose, onSaved, setMsg }) {
  const [title, setTitle] = useState(article.title);
  const [excerpt, setExcerpt] = useState(article.excerpt || "");
  const [cover, setCover] = useState(article.cover || "");
  const [tags, setTags] = useState(article.tags || "");
  const [body, setBody] = useState(article.body);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/news/${article.id}`, { title, excerpt, cover, tags, body });
      onSaved();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(16px)" }} onClick={onClose}>
      <div className="glass-strong rounded-3xl p-7 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Edit article</div>
          <button onClick={onClose} className="text-white/40 hover:text-white">Close</button>
        </div>
        <div className="mt-5 space-y-3">
          <FieldRow label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} className={cellInput + " w-full"} /></FieldRow>
          <FieldRow label="Excerpt"><textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} className={cellInput + " w-full"} /></FieldRow>
          <FieldRow label="Cover URL"><input value={cover} onChange={(e) => setCover(e.target.value)} className={cellInput + " w-full"} /></FieldRow>
          <FieldRow label="Tags (comma-separated)"><input value={tags} onChange={(e) => setTags(e.target.value)} className={cellInput + " w-full"} /></FieldRow>
          <FieldRow label="Body (Markdown)"><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} className={cellInput + " w-full font-mono text-[12px]"} /></FieldRow>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full glass text-[13px]">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-full bg-[#0A84FF] text-white text-[13px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PartnerInboxTab({ setMsg }) {
  const [msgs, setMsgs] = useState([]);
  const load = async () => {
    try { const r = await api.get("/admin/partner-messages"); setMsgs(r.data.messages || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try { await api.patch(`/admin/partner-messages/${id}`, body); await load(); setMsg({ type: "ok", text: "Updated" }); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/admin/partner-messages/${id}`); await load(); }
    catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
  };

  return (
    <div className="space-y-3" data-testid="partner-inbox-tab">
      {msgs.length === 0 && <div className="glass rounded-3xl p-8 text-center text-white/50">No partner messages yet.</div>}
      {msgs.map((m) => (
        <div key={m.id} className="glass rounded-3xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[15px] font-semibold">{m.name}</div>
                <div className="text-[12px] text-white/50">&lt;{m.email}&gt;</div>
                {m.company && <div className="text-[12px] text-white/50">· {m.company}</div>}
                <div className="text-[10px] uppercase tracking-widest text-white ml-2">{m.partnership_type || "general"}</div>
              </div>
              <div className="mt-3 text-[14px] text-white/80 whitespace-pre-wrap leading-relaxed">{m.message}</div>
              {m.admin_notes && <div className="mt-3 text-[12px] text-white/50 italic">Notes: {m.admin_notes}</div>}
              <div className="mt-3 text-[11px] text-white/40">{(m.created_at || "").slice(0, 16)}</div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <select value={m.status || "new"} onChange={(e) => patch(m.id, { status: e.target.value })} className={cellSelect}>
                <option value="new">new</option>
                <option value="replied">replied</option>
                <option value="archived">archived</option>
                <option value="spam">spam</option>
              </select>
              <button onClick={() => { const n = prompt("Admin notes", m.admin_notes || ""); if (n !== null) patch(m.id, { admin_notes: n }); }}
                className="text-white/60 hover:text-white text-[12px]">notes</button>
              <button onClick={() => remove(m.id)} className="text-red-300 hover:text-red-200 text-[12px]">delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab({ reloadOverview, setMsg }) {
  const [pending, setPending] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/stake-reviews");
      setPending(r.data.pending || []);
      setReviewed(r.data.reviewed || []);
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Load failed" }); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    const label = decision === "approve" ? "approve" : decision === "reject" ? "REJECT + unlink" : "reset to pending";
    if (!window.confirm(`${label} this Stake link?`)) return;
    setBusy(id);
    try {
      await api.patch(`/admin/stake-reviews/${id}`, { decision, notes: notes[id] || null });
      setMsg({ type: "ok", text: `Link ${decision}d` });
      setNotes((n) => ({ ...n, [id]: "" }));
      await load();
      reloadOverview && reloadOverview();
    } catch (e) { setMsg({ type: "err", text: e.response?.data?.detail || "Failed" }); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-xl font-bold tracking-tight">
          Pending review <span className="ml-2 text-[12px] font-normal text-amber-300">{pending.length} unverified</span>
        </h2>
        <p className="mt-1 text-[13px] text-white/50">
          Yellow-dot links the automatic check couldn't verify. Open the Stake profile yourself —
          approve if it's real and public, reject (unlinks them) if not.
        </p>
        <div className="mt-4 space-y-3">
          {pending.length === 0 && <div className="glass rounded-3xl p-8 text-center text-white/50">Queue is clear. Nothing to review.</div>}
          {pending.map((u) => (
            <div key={u.id} className="glass rounded-3xl p-6">
              <div className="flex items-center gap-3 flex-wrap">
                {u.avatar && <img src={u.avatar} className="w-8 h-8 rounded-full" alt="" />}
                <div className="font-semibold">{u.global_name || u.username}</div>
                <div className="text-white/40 text-[12px]">{u.discord_id}</div>
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass text-[12px] text-amber-300">
                  <span className="w-2 h-2 rounded-full" style={{ background: "#fbbf24", boxShadow: "0 0 8px #fbbf24" }} />
                  @{u.stake_username}
                </div>
                <div className="text-white/40 text-[12px]">linked {(u.stake_linked_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="mt-4 flex flex-col md:flex-row gap-2">
                <input value={notes[u.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [u.id]: e.target.value }))}
                  placeholder="Review note (optional)" className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-[13px] outline-none focus:border-[#0A84FF]/70" />
                <div className="flex gap-2">
                  <button disabled={busy === u.id} onClick={() => decide(u.id, "approve")}
                    className="inline-flex items-center gap-1 rounded-full px-4 py-2 bg-emerald-400 text-black text-[12px] font-semibold hover:bg-emerald-300 disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button disabled={busy === u.id} onClick={() => decide(u.id, "reject")}
                    className="inline-flex items-center gap-1 rounded-full px-4 py-2 bg-red-400/90 text-black text-[12px] font-semibold hover:bg-red-300 disabled:opacity-50">
                    <Trash2 className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold tracking-tight">Recently reviewed</h2>
        <div className="mt-4 space-y-2">
          {reviewed.length === 0 && <div className="text-white/40 text-[13px]">No reviews yet.</div>}
          {reviewed.map((u) => (
            <div key={u.id} className="glass rounded-2xl px-5 py-3 flex items-center gap-3 flex-wrap text-[13px]">
              <span className={`w-2 h-2 rounded-full shrink-0 ${u.stake_verified ? "bg-emerald-400" : "bg-white/20"}`} />
              <span className="font-semibold">{u.global_name || u.username}</span>
              {u.stake_username
                ? <span className="text-white/60">@{u.stake_username}</span>
                : <span className="text-white/30 italic">unlinked</span>}
              <span className={`uppercase tracking-widest text-[10px] ${u.stake_review_status === "approved" ? "text-emerald-300" : u.stake_review_status === "rejected" ? "text-red-300" : "text-white/40"}`}>
                {u.stake_review_status || "—"}
              </span>
              {u.stake_review_notes && <span className="text-white/40 italic">“{u.stake_review_notes}”</span>}
              <button onClick={() => decide(u.id, "reset")} className="ml-auto text-white/50 hover:text-white text-[12px]">reset</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
